import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { call, put, takeEvery } from "redux-saga/effects";
import { Officer } from "../../interfaces";
import { Holder } from "../../interfaces/Holder";
import { Paginated } from "../../interfaces/Paginated";
import { googleFunctionService } from "../../services/axios";
import * as sagaActions from "./sagaActions";
import { actions } from "./slice";
import * as HolderActionTypes from "./types";

export function* getAllOfficerSaga() {
  yield put(
    actions.setLoader({ fetchableProp: "allOfficers", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<Paginated<Officer>> = yield call(
      googleFunctionService.get,
      "/officer"
    );
    yield put(actions.setPaginatedOfficers(data));
  } catch (err) {
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "allOfficers", isLoading: false })
    );
  }
}

export function* createOfficerSaga({
  payload,
}: PayloadAction<Omit<Holder, "uid">>) {
  yield put(
    actions.setLoader({ fetchableProp: "createdOfficer", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<Officer> = yield call(
      googleFunctionService.post,
      "/officer",
      payload
    );
    yield put(actions.createOfficer(data));
  } catch (err) {
    yield put(
      actions.setError({ fetchableProp: "createdOfficer", error: err })
    );
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "createdOfficer", isLoading: false })
    );
  }
}

export function* updateOfficerSaga({
  payload,
}: ReturnType<typeof sagaActions.updateOfficer>) {
  yield put(
    actions.setLoader({ fetchableProp: "updatedOfficer", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<Officer> = yield call(
      googleFunctionService,
      `/officer/${payload.uid}`,
      {
        method: "PUT",
        data: payload,
      }
    );
    yield put(actions.updateOfficer(data));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "updatedOfficer", error }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "updatedOfficer", isLoading: false })
    );
  }
}

export function* deleteOfficerSaga({
  payload,
}: ReturnType<typeof sagaActions.deleteOfficerById>) {
  yield put(
    actions.setLoader({ fetchableProp: "deletedOfficer", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<Officer> = yield call(
      googleFunctionService.delete,
      `/user/${payload}`
    );
    yield put(actions.deleteOfficer(data));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "deletedOfficer", error }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "deletedOfficer", isLoading: false })
    );
  }
}

export function* resetPasswordSaga({
  payload,
}: ReturnType<typeof sagaActions.resetPasswordById>) {
  yield put(
    actions.setLoader({ fetchableProp: "passwordReset", isLoading: true })
  );

  try {
    yield call(googleFunctionService.patch, `/user/${payload}/reset-password`);
    yield put(actions.resetPassword(true));
  } catch (err) {
    yield put(actions.resetPassword(false));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "passwordReset", isLoading: false })
    );
  }
}

export function* getOfficerByIdSaga({
  payload,
}: ReturnType<typeof sagaActions.loadOfficerById>) {
  yield put(actions.setLoader({ fetchableProp: "officer", isLoading: true }));

  try {
    const { data }: AxiosResponse<Officer> = yield call(
      googleFunctionService.get,
      `/user/${payload}`
    );
    yield put(actions.setOfficer(data));
  } catch (error) {
    yield put(actions.setError({ fetchableProp: "officer", error }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "officer", isLoading: false })
    );
  }
}

export default function* rootSaga() {
  yield takeEvery(
    HolderActionTypes.fetch_paginated_officers_saga,
    getAllOfficerSaga
  );
  yield takeEvery(
    HolderActionTypes.fetch_officer_by_id_saga,
    getOfficerByIdSaga
  );
  yield takeEvery(HolderActionTypes.create_new_officer_saga, createOfficerSaga);
  yield takeEvery(HolderActionTypes.delete_officer_saga, deleteOfficerSaga);
  yield takeEvery(HolderActionTypes.update_officer_saga, updateOfficerSaga);
  yield takeEvery(
    HolderActionTypes.reset_password_officer_saga,
    resetPasswordSaga
  );
}
